/**
 * Destaque no mapa enquanto a ficha de um foco está aberta: o círculo de
 * 375 m usado na conta do uso do solo (tracejado) e os imóveis do CAR que o
 * tocam, cada um com a cor da linha dele na ficha. Os que contêm o ponto do
 * foco têm contorno mais grosso; os cancelados, tracejado. O mapa só se move
 * no clique na linha de um imóvel (terreno/aproximar.ts).
 *
 * Uma camada por mapa, criada no primeiro uso, entre a máscara do filtro dos
 * focos e os pontos (Z.TERRENO). Só uma ficha por vez: quem mostra recebe uma
 * função que limpa, e ela só limpa se o destaque ainda for o dela.
 */
import Feature from 'ol/Feature';
import type OlMap from 'ol/Map';
import type { Extent } from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import Polygon from 'ol/geom/Polygon';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import { GOES_PROJ, lonLatToMap, registerGoesProjection } from '../map/projection';
import { Z } from '../map/zIndex';
import { M_POR_GRAU } from './api';
import type { ImovelCar } from './api';

/** Cores dos imóveis, na ordem da ficha: distintas da legenda do MapBiomas e do laranja dos focos. */
const CORES = ['#22d3ee', '#c084fc', '#f472b6', '#facc15', '#4ade80', '#60a5fa'];

export function corDoImovel(i: number): string {
  return CORES[i % CORES.length]!;
}

function comAlfa(hex: string, a: number): string {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
}

interface Estado {
  layer: VectorLayer<VectorSource>;
  dono: object | null;
  realce: string | null;
}

const porMapa = new WeakMap<OlMap, Estado>();
const formato = new GeoJSON();

function estilo(estado: Estado) {
  return (f: Feature): Style => {
    if (f.get('circulo')) {
      return new Style({ stroke: new Stroke({ color: 'rgba(255,255,255,0.9)', width: 1.5, lineDash: [5, 4] }) });
    }
    const cor = f.get('cor') as string;
    const realcado = estado.realce === f.get('cod');
    const cancelado = f.get('status') === 'CA';
    return new Style({
      zIndex: realcado ? 2 : f.get('contem') ? 1 : 0,
      fill: new Fill({ color: comAlfa(cor, realcado ? 0.28 : 0.12) }),
      stroke: new Stroke({
        color: cor,
        width: realcado ? 3.5 : f.get('contem') ? 2.5 : 1.5,
        lineDash: cancelado ? [6, 4] : undefined,
      }),
    });
  };
}

function camada(map: OlMap): Estado {
  let estado = porMapa.get(map);
  if (!estado) {
    const layer = new VectorLayer({ source: new VectorSource(), zIndex: Z.TERRENO, properties: { id: 'terreno' } });
    estado = { layer, dono: null, realce: null };
    layer.setStyle(estilo(estado) as never);
    map.addLayer(layer);
    porMapa.set(map, estado);
  }
  return estado;
}

/** O círculo do raio em volta do ponto, na projeção do mapa. */
function circulo(lat: number, lon: number, raioM: number, n = 64): Polygon {
  const dlat = raioM / M_POR_GRAU;
  const dlon = raioM / (M_POR_GRAU * Math.cos((lat * Math.PI) / 180));
  const anel = Array.from({ length: n + 1 }, (_, k) => {
    const a = (2 * Math.PI * k) / n;
    return lonLatToMap(lon + dlon * Math.cos(a), lat + dlat * Math.sin(a));
  });
  return new Polygon([anel]);
}

/** Mostra o círculo e os imóveis; devolve a função que limpa. */
export function mostrarTerreno(map: OlMap, lat: number, lon: number, raioM: number, imoveis: ImovelCar[]): () => void {
  registerGoesProjection();
  const estado = camada(map);
  const dono = {};
  estado.dono = dono;
  estado.realce = null;
  const fonte = estado.layer.getSource()!;
  fonte.clear();
  const feicoes: Feature[] = [new Feature({ geometry: circulo(lat, lon, raioM), circulo: true })];
  imoveis.forEach((im, i) => {
    if (!im.geom) return;
    try {
      const geometry = formato.readGeometry(im.geom, { dataProjection: 'EPSG:4326', featureProjection: GOES_PROJ });
      feicoes.push(new Feature({ geometry, cod: im.cod, cor: corDoImovel(i), contem: im.contem, status: im.status }));
    } catch {
      /* contorno ilegível: a linha da ficha continua */
    }
  });
  fonte.addFeatures(feicoes);
  return () => {
    if (estado.dono !== dono) return;
    estado.dono = null;
    estado.realce = null;
    fonte.clear();
  };
}

/** Engrossa o contorno de um imóvel (ao passar o mouse ou tocar na linha dele); null desfaz. */
export function realcarImovel(map: OlMap, cod: string | null): void {
  const estado = porMapa.get(map);
  if (!estado || estado.realce === cod) return;
  estado.realce = cod;
  estado.layer.changed();
}

/** A extensão (na projeção do mapa) do contorno de um imóvel mostrado agora, ou null. */
export function extensaoDoImovel(map: OlMap, cod: string): Extent | null {
  const estado = porMapa.get(map);
  const f = estado?.layer.getSource()?.getFeatures().find((x) => x.get('cod') === cod);
  return f?.getGeometry()?.getExtent() ?? null;
}
